import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import HomeLayout from '../../asset/layout/HomeLayout';
import CustomButton from '../../components/mui/CustomButton';
import {
  Box,
  Card,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import MyTable from '../../components/Table';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SearchBar from '../../components/mui/SearchBar';
import { MasterCard } from '../../components/Cards/MasterCard';
import { useDispatch } from 'react-redux';
import { getAllTransactions } from '../../actions/transactionsAuth';
import { useNavigate } from 'react-router-dom';
import ResponsiveDatePickers from '../../components/mui/PichDate';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { format } from 'date-fns-tz';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

const iconData = [
  { src: '/CardTransfer.svg', alt: 'Total', name: 'Total' },
  { src: '/CardRecive.svg', alt: 'Pending', name: 'Pending' },
  { src: '/CardSend.svg', alt: 'Failed', name: 'Failed' },
  { src: '/ClipboardRemove.svg', alt: 'This week', name: 'This week' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'SUCCESS', label: 'Success' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'EXPIRED', label: 'Expired' },
];

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.05, duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  }),
};

const PanelCard = ({ children, sx = {} }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: '24px',
      border: '1px solid',
      borderColor: alpha('#11409B', 0.08),
      bgcolor: '#fff',
      boxShadow: '0 4px 32px rgba(15, 23, 42, 0.06)',
      overflow: 'hidden',
      ...sx,
    }}
  >
    {children}
  </Card>
);

const formatPaymentInstrument = (value) => {
  if (value === null || value === undefined || value === '') return value;
  if (String(value).trim().toUpperCase() === 'DEFAULT') return 'UPI';
  return value;
};

const formatDisplayStatus = (status) => {
  if (status === null || status === undefined || status === '') return '--';
  if (String(status).toUpperCase() === 'PENDING') return 'EXPIRED';
  return status;
};

const normalizeTransaction = (item) => {
  const tx =
    item?.transaction && typeof item.transaction === 'object'
      ? item.transaction
      : {};
  return {
    ...tx,
    ...item,
    id: item?.id || tx?.id,
    amount: item?.amount ?? tx?.amount,
    status: item?.status ?? tx?.status,
    paymentType: item?.paymentType ?? tx?.paymentType,
    entryType: item?.entryType ?? tx?.entryType,
    orderId: item?.orderId ?? tx?.orderId,
    userName:
      item?.userName ??
      item?.user?.name ??
      item?.user?.username ??
      item?.customerDetails?.customerId?.username,
    userEmail:
      item?.userEmail ??
      item?.user?.email ??
      item?.customerDetails?.customerId?.email,
    userPhone:
      item?.userPhone ??
      item?.user?.phone ??
      item?.customerDetails?.customerPhone,
    createdAt: new Date(item?.createdAt ?? tx?.createdAt),
    updatedAt: new Date(item?.updatedAt ?? tx?.updatedAt),
    orders: item?.orders,
    paymentCharge:
      item?.paymentCharge ??
      (Array.isArray(item?.orders) ? item.orders[0]?.paymentCharge : undefined),
    paymentInstrument: formatPaymentInstrument(
      item?.paymentInstrument ??
        (Array.isArray(item?.orders) ? item.orders[0]?.paymentInstrument : undefined)
    ),
  };
};

const Transactions = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedDateTo, setSelectedDateTo] = useState(null);
  const [selectedDateFrom, setSelectedDateFrom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [pendingTransactions, setPendingTransactions] = useState(0);
  const [failedTransactions, setFailedTransactions] = useState(0);
  const [thisWeekTransactions, setThisWeekTransactions] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [countPerPage, setCountPerPage] = useState(10);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem('token')) {
      navigate('/');
    }
  }, [navigate]);

  useEffect(() => {
    setLoading(true);
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
    startOfWeek.setHours(0, 0, 0, 0);
    const endOfWeek = new Date(today);
    endOfWeek.setDate(endOfWeek.getDate() + (6 - endOfWeek.getDay()));
    endOfWeek.setHours(23, 59, 59, 999);

    const queryPayload = { page: 0, size: 1000 };

    if (searchTerm?.trim()) queryPayload.search = searchTerm.trim();
    if (statusFilter) queryPayload.status = statusFilter;
    if (selectedDateFrom) {
      queryPayload.createdFrom = new Date(selectedDateFrom).toISOString();
    }
    if (selectedDateTo) {
      queryPayload.createdTo = new Date(selectedDateTo).toISOString();
    }

    dispatch(
      getAllTransactions(
        queryPayload,
        (data) => {
          const transactionsList =
            data?.content ||
            data?.data?.content ||
            data?.data?.data?.content ||
            data?.data?.data?.Transactions ||
            data?.data?.Transactions ||
            data?.data?.data ||
            data?.data ||
            [];

          const transactionsWithDates = (
            Array.isArray(transactionsList) ? transactionsList : []
          ).map(normalizeTransaction);

          const pending = transactionsWithDates.filter(
            (t) => (t.orderStatus || t.status) === 'PENDING'
          );
          const failed = transactionsWithDates.filter((t) => {
            const s = t.status || t.orderStatus;
            return s === 'FAILED' || s === 'EXPIRED';
          });
          const thisWeek = transactionsWithDates.filter((t) => {
            const d = t.createdAt;
            return (
              d instanceof Date &&
              !Number.isNaN(d.getTime()) &&
              d >= startOfWeek &&
              d <= endOfWeek
            );
          });

          setTransactions(transactionsWithDates);
          setTotalTransactions(transactionsWithDates.length);
          setPendingTransactions(pending.length);
          setFailedTransactions(failed.length);
          setThisWeekTransactions(thisWeek.length);
          setLoading(false);
        },
        (err) => {
          console.log('Error fetching transactions:', err);
          setLoading(false);
        }
      )
    );
  }, [searchTerm, selectedDateFrom, selectedDateTo, statusFilter, dispatch]);

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleSearchClear = () => {
    setSearchTerm('');
    setSelectedDateFrom(null);
    setSelectedDateTo(null);
    setStatusFilter('');
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(transactions.length / countPerPage) || 1;

  const paginatedData = transactions.slice(
    (currentPage - 1) * countPerPage,
    currentPage * countPerPage
  );

  const formatDateIndian = (dateString) => {
    const date = new Date(dateString);
    return format(date, 'dd/MM/yyyy', { timeZone: 'Asia/Kolkata' });
  };

  const exportToExcel = () => {
    const header = [
      'Transaction ID',
      'Order ID',
      'Customer',
      'Email',
      'Phone',
      'Date',
      'Amount',
      'Transaction charge',
      'Payment instrument',
      'Status',
      'Payment Type',
    ];

    const dataToExport = transactions.map((row) => {
      const rawAmount = row?.amount ?? row?.orderAmount ?? 0;
      const amount =
        Number(rawAmount) > 999 ? Number(rawAmount) / 100 : Number(rawAmount);
      return [
        row?.id || row?.transaction?.id,
        row?.orderId,
        row?.userName || row?.user?.name,
        row?.userEmail || row?.user?.email,
        row?.userPhone || row?.user?.phone,
        row.createdAt ? formatDateIndian(row.createdAt) : '--',
        amount,
        row?.paymentCharge ?? '--',
        row?.paymentInstrument ?? '--',
        formatDisplayStatus(row?.status ?? row?.orderStatus),
        row?.paymentType ?? '--',
      ];
    });

    dataToExport.unshift(header);
    const worksheet = XLSX.utils.aoa_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Transactions');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, 'Transactions_data.xlsx');
  };

  const statCounts = [
    totalTransactions,
    pendingTransactions,
    failedTransactions,
    thisWeekTransactions,
  ];

  const selectSx = {
    height: '2.75rem',
    borderRadius: '12px',
    bgcolor: '#fff',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: alpha('#11409B', 0.2),
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#11409B',
    },
  };

  return (
    <HomeLayout>
      <Box
        sx={{
          minHeight: '100%',
          background:
            'linear-gradient(180deg, #f0f4fa 0%, #f5f7fa 35%, #eef2f7 100%)',
          mx: -3,
          mt: -3,
          px: 3,
          pt: 3,
          pb: 4,
        }}
      >
        {/* Hero */}
        <motion.div initial="hidden" animate="visible" variants={fadeUp}>
          <Box
            sx={{
              p: { xs: 3, md: 4 },
              mb: 3,
              borderRadius: '24px',
              background:
                'linear-gradient(135deg, #0c3483 0%, #11409B 40%, #1e40af 70%, #2563eb 100%)',
              color: '#fff',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(17, 64, 155, 0.35)',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: -40,
                right: -20,
                width: 180,
                height: 180,
                borderRadius: '50%',
                bgcolor: alpha('#fff', 0.06),
              }}
            />
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
              spacing={2}
              sx={{ position: 'relative', zIndex: 1 }}
            >
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <ReceiptLongOutlinedIcon />
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, letterSpacing: '0.1em', opacity: 0.85 }}
                  >
                    TRANSACTIONS
                  </Typography>
                </Stack>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 800, letterSpacing: '-0.03em' }}
                >
                  Payment transactions
                </Typography>
                <Typography sx={{ mt: 1, opacity: 0.82, maxWidth: 520 }}>
                  Track all payments, filter by status or date, and expand rows
                  to view linked orders.
                </Typography>
              </Box>
              <CustomButton
                variant="contained"
                onClick={exportToExcel}
                startIcon={<FileDownloadOutlinedIcon />}
                sx={{
                  height: '44px',
                  borderRadius: '12px',
                  fontWeight: 600,
                  textTransform: 'none',
                  bgcolor: alpha('#fff', 0.15),
                  border: `1px solid ${alpha('#fff', 0.25)}`,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: alpha('#fff', 0.22) },
                }}
              >
                Export transactions
              </CustomButton>
            </Stack>
          </Box>
        </motion.div>

        {/* Stats */}
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          {iconData.map((data, index) => (
            <Grid item key={data.name} xs={12} sm={6} md={3}>
              <motion.div
                custom={index}
                initial="hidden"
                animate="visible"
                variants={fadeUp}
                style={{ height: '100%' }}
              >
                <MasterCard
                  {...data}
                  count={statCounts[index]}
                />
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Filters */}
        <motion.div custom={4} initial="hidden" animate="visible" variants={fadeUp}>
          <PanelCard sx={{ mb: 3, p: { xs: 2, md: 2.5 } }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <FilterListOutlinedIcon sx={{ color: '#11409B', fontSize: 20 }} />
              <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>
                Filters
              </Typography>
            </Stack>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <SearchBar
                  onSearch={handleSearch}
                  searchTerm={searchTerm}
                  height="2.75rem"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel id="transaction-status-label">Status</InputLabel>
                  <Select
                    labelId="transaction-status-label"
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                    sx={selectSx}
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value || 'all'} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2.5}>
                <ResponsiveDatePickers
                  selectedDate={selectedDateFrom}
                  setSelectedDate={(d) => {
                    setSelectedDateFrom(d);
                    setCurrentPage(1);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.5}>
                <ResponsiveDatePickers
                  selectedDate={selectedDateTo}
                  setSelectedDate={(d) => {
                    setSelectedDateTo(d);
                    setCurrentPage(1);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <CustomButton
                  variant="outlined"
                  onClick={handleSearchClear}
                  fullWidth
                  sx={{
                    height: '2.75rem',
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: alpha('#11409B', 0.25),
                    color: '#11409B',
                    '&:hover': {
                      borderColor: '#11409B',
                      bgcolor: alpha('#11409B', 0.04),
                    },
                  }}
                >
                  Clear
                </CustomButton>
              </Grid>
            </Grid>
          </PanelCard>
        </motion.div>

        {/* Table */}
        <motion.div custom={5} initial="hidden" animate="visible" variants={fadeUp}>
          {loading ? (
            <PanelCard>
              <Stack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
                <CircularProgress sx={{ color: '#11409B' }} />
                <Typography sx={{ mt: 2, color: '#64748b' }}>
                  Loading transactions…
                </Typography>
              </Stack>
            </PanelCard>
          ) : transactions.length === 0 ? (
            <PanelCard>
              <Stack alignItems="center" sx={{ py: 8, px: 3 }}>
                <ReceiptLongOutlinedIcon
                  sx={{ fontSize: 56, color: '#cbd5e1', mb: 2 }}
                />
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                  No transactions found
                </Typography>
                <Typography sx={{ color: '#64748b', mt: 1, textAlign: 'center' }}>
                  Try adjusting your search, status, or date filters.
                </Typography>
              </Stack>
            </PanelCard>
          ) : (
            <MyTable transactions={paginatedData} />
          )}
        </motion.div>

        {/* Pagination */}
        {!loading && transactions.length > 0 && (
          <motion.div custom={6} initial="hidden" animate="visible" variants={fadeUp}>
            <PanelCard sx={{ mt: 3, p: 2 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
              >
                <Typography sx={{ color: '#64748b', fontWeight: 500 }}>
                  Showing {(currentPage - 1) * countPerPage + 1}–
                  {Math.min(currentPage * countPerPage, transactions.length)} of{' '}
                  {transactions.length}
                </Typography>

                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    Rows per page
                  </Typography>
                  <Box
                    component="select"
                    value={countPerPage}
                    onChange={(e) => {
                      setCountPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    sx={{
                      border: `1px solid ${alpha('#11409B', 0.2)}`,
                      borderRadius: '10px',
                      px: 1.5,
                      py: 0.75,
                      fontSize: '0.875rem',
                      bgcolor: '#fff',
                      outline: 'none',
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={30}>30</option>
                  </Box>

                  <Stack direction="row" alignItems="center" spacing={1}>
                    <IconButton
                      onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                      disabled={currentPage === 1}
                      sx={{
                        border: '1px solid',
                        borderColor: alpha('#11409B', 0.15),
                        borderRadius: '12px',
                        '&:hover': { bgcolor: alpha('#11409B', 0.06) },
                      }}
                    >
                      <KeyboardArrowLeftIcon />
                    </IconButton>
                    <Typography sx={{ fontWeight: 600, minWidth: 48, textAlign: 'center' }}>
                      {currentPage} / {totalPages}
                    </Typography>
                    <IconButton
                      onClick={() =>
                        setCurrentPage((p) => Math.min(p + 1, totalPages))
                      }
                      disabled={currentPage >= totalPages}
                      sx={{
                        border: '1px solid',
                        borderColor: alpha('#11409B', 0.15),
                        borderRadius: '12px',
                        '&:hover': { bgcolor: alpha('#11409B', 0.06) },
                      }}
                    >
                      <KeyboardArrowRightIcon />
                    </IconButton>
                  </Stack>
                </Stack>
              </Stack>
            </PanelCard>
          </motion.div>
        )}
      </Box>
    </HomeLayout>
  );
};

export default Transactions;
